import React, { useState } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useDebouncedCallback } from 'use-debounce'
import { ICarrierOnChange, searchCarriers } from 'apps/marketplace/utils/api/search'
import useDefaultOptions from '../utils/hooks/useDefaultOptions'
import { ICarrierResult, ICarrierOption } from './types'

interface ILegacyCarrierAutosuggestProps extends IAutosuggestWrapperProps<ICarrierOnChange> {
  defaultValue?: ICarrierResult
  carrierOptions?: ICarrierOption[]
}

const LegacyCarrierAutosuggest = ({
  carrierOptions,
  defaultValue,
  disabled = false,
  error,
  label = 'Carrier',
  onChange: onChangeProp,
  required,
  value: valueProp,
}: ILegacyCarrierAutosuggestProps) => {
  let convertedDefaultValue
  if (defaultValue) {
    convertedDefaultValue = {
      id: defaultValue.id,
      text: defaultValue.name || '',
      groupIndex: 0,
    }
  }

  // Setting defaultOptions
  // Setting defaultOptions to a array if a default value is passing
  const { defaultOptions } = useDefaultOptions({
    defaultValue,
    convertedDefaultValue,
    carrierOptions,
  })
  // const defaultOptions = setDefaultOptions(defaultValue, convertedDefaultValue, carrierOptions)

  const [carrierResults, setCarrierResults] = useState<IAutocompleteOption[] | undefined>(
    defaultOptions
  )

  const fetchCarriers = async (searchTerm: string) => {
    const results = await searchCarriers(searchTerm)
    const options = getAutosuggestOptions({
      options: results.namesearch,
      groupIndex: 1,
      mapOption: ({ dotNumber, id, mcNumber, name }: ICarrierResult) => {
        const autocompleteText = [name]
        if (id) autocompleteText.push(`ID: ${id}`)
        if (mcNumber) autocompleteText.push(`MC: ${mcNumber}`)
        if (dotNumber) autocompleteText.push(`DOT: ${dotNumber}`)
        return { id, text: autocompleteText.join(' | ') }
      },
    })
    setCarrierResults(options)
  }

  const changeHandler = async (value?: string) => {
    if (value && value.length > 0) {
      await fetchCarriers(value)
      const carrierResult = carrierResults?.find(result => result.text === value)
      if (carrierResult) {
        onChangeProp({
          id: carrierResult.id,
          name: carrierResult.text,
        })
      }
    } else {
      onChangeProp({})
    }
  }

  const [debouncedChangeHandler] = useDebouncedCallback((value: string) => {
    changeHandler(value)
  }, 500)

  return (
    <Autosuggest
      id='carrier'
      dataTest='carrier-autosuggest'
      value={convertedDefaultValue || valueProp}
      placeholder='Enter carrier name'
      options={carrierResults}
      clearable
      label={label}
      onChange={onChangeProp}
      onInputChange={debouncedChangeHandler}
      large={false}
      disabled={disabled}
      required={required}
      error={error}
    />
  )
}

export default LegacyCarrierAutosuggest
